/**
 * User Store
 * 管理用户信息和登录状态
 */

import { defineStore } from 'pinia'
import type { LoginSuccessData, ChannelInfo, GameDesk, VideoServer } from '@/types/websocket'
import localforage from 'localforage'
import { getDeviceId, getPlatform } from '@/utils/device'
import '@/types/config' // 导入 Window 类型扩展
import * as authApi from '@/services/api/auth'

interface UserState {
  memberId: string // 会员ID (会员唯一标识)
  userName: string // 用户名 (显示名称,从 USLO02 获取)
  userCode: string // 用户代码 (从 USLO02 获取)
  balance: number
  isLoggedIn: boolean
  gamedeskList: GameDesk[]
  channelList: ChannelInfo[]
  serverList: VideoServer[] // 视频服务器列表
  serverTime: number
  userType: string // 用户类型 ('5'=普通用户, '11'=游客)
  vipType: string // VIP类型 ('0'=电话会员, '1'=网投会员, '2'=高级电话会员)
  agentType: string // 客服类型 ('0'=不显示, '1'=显示联系方式, '2'=显示公司客服)
  adsList: string[] // 广告列表 (从 USLO02 获取)
  // 效力ID字段 (用于防止旧数据覆盖新数据)
  accountServerId: number // 服务器运行ID
  memberBalanceValidityId: number // 会员余额效力ID
  memberDatabaseBalance: number // 会员数据库余额
  memberBettingValidityId: number // 会员投注效力ID
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    memberId: '',
    userName: '',
    userCode: '',
    balance: 0,
    isLoggedIn: false,
    gamedeskList: [],
    channelList: [],
    serverList: [],
    serverTime: 0,
    userType: '5', // 默认普通用户
    vipType: '0', // 默认电话会员
    agentType: '0', // 默认不显示客服
    adsList: [], // 默认空广告列表
    // 效力ID初始值
    accountServerId: -1,
    memberBalanceValidityId: 0,
    memberDatabaseBalance: 0,
    memberBettingValidityId: 0,
  }),

  getters: {
    /**
     * 格式化余额 (仅保留两位小数)
     * 注意: 这只是基础格式化,不应该包含业务逻辑(如"k"格式)
     * "k"格式等显示逻辑应该在组件层使用 @/utils/balance 工具函数
     */
    formattedBalance: state => {
      return state.balance.toFixed(2)
    },

    /**
     * 是否已登录
     */
    loggedIn: state => state.isLoggedIn && !!state.memberId,
  },

  actions: {
    /**
     * 设置用户信息 (登录成功后调用)
     */
    async setUserInfo(data: LoginSuccessData) {
      this.memberId = data.memberId
      this.userName = data.userName
      this.userCode = data.userCode
      this.balance = data.balance
      this.gamedeskList = data.gamedeskList
      this.channelList = data.channelList
      this.serverList = data.serverList || []
      this.serverTime = data.serverTime
      this.vipType = data.vipType || '0'
      this.agentType = data.agentType || '0'
      this.adsList = data.adsList || []
      this.isLoggedIn = true

      // 保存代理网站链接到 localStorage
      if (data.betReportIp) {
        try {
          // 从完整 URL 中提取域名 (例如: http://ag.xmgm888.com/WinCall -> http://ag.xmgm888.com)
          const url = new URL(data.betReportIp)
          const domain = `${url.protocol}//${url.host}`
          localStorage.setItem('betReportIp', domain)
        } catch {
          // 如果不是有效的 URL,直接存储原始值
          localStorage.setItem('betReportIp', data.betReportIp)
        }
      }

      // 持久化到 IndexedDB
      await this.persistUserInfo()
    },

    /**
     * 更新余额
     */
    updateBalance(balance: number) {
      this.balance = balance
      this.persistUserInfo()
    },

    /**
     * 登出 (清空所有用户数据)
     */
    async logout() {
      // 1. 断开 WebSocket 连接
      const { useWebSocketStore } = await import('./websocket')
      const wsStore = useWebSocketStore()
      wsStore.disconnect()

      // 2. 清空用户数据
      this.memberId = ''
      this.userName = ''
      this.userCode = ''
      this.balance = 0
      this.isLoggedIn = false
      this.gamedeskList = []
      this.channelList = []
      this.serverList = []
      this.serverTime = 0
      this.userType = '5'
      this.vipType = '0'
      this.agentType = '0'
      this.adsList = []

      // 3. 清空效力ID字段 (重置为初始值)
      this.accountServerId = -1
      this.memberBalanceValidityId = 0
      this.memberDatabaseBalance = 0
      this.memberBettingValidityId = 0

      // 4. 清空台桌数据
      const { useTableStore } = await import('./table')
      const tableStore = useTableStore()
      tableStore.clear()

      // 5. 清除持久化数据
      await localforage.removeItem('userInfo')
    },

    /**
     * 持久化用户信息到 IndexedDB
     */
    async persistUserInfo() {
      try {
        // 使用 JSON.parse(JSON.stringify()) 进行深拷贝,移除无法序列化的内容
        const userInfo = JSON.parse(
          JSON.stringify({
            memberId: this.memberId,
            userName: this.userName,
            userCode: this.userCode,
            balance: this.balance,
            gamedeskList: this.gamedeskList,
            channelList: this.channelList,
            updatedAt: Date.now(),
          })
        )

        await localforage.setItem('userInfo', userInfo)
      } catch (error) {
        console.error('[User] 持久化用户信息失败:', error)
      }
    },

    /**
     * 从缓存恢复用户信息
     */
    async restoreFromCache() {
      const cached = await localforage.getItem<{
        memberId: string
        userName: string
        userCode: string
        balance: number
        gamedeskList: GameDesk[]
        channelList: ChannelInfo[]
        updatedAt: number
      }>('userInfo')

      if (cached && Date.now() - cached.updatedAt < 86400000) {
        // 24小时内有效
        this.memberId = cached.memberId
        this.userName = cached.userName || ''
        this.userCode = cached.userCode || ''
        this.balance = cached.balance ?? 0
        this.gamedeskList = cached.gamedeskList || []
        this.channelList = cached.channelList || []
        this.isLoggedIn = true
        return true
      }

      return false
    },

    /**
     * 发送登录消息
     */
    async login(username: string, password: string, userType: string = '5') {
      // 保存用户类型
      this.userType = userType

      const { useWebSocketStore } = await import('./websocket')
      const wsStore = useWebSocketStore()

      // 确保已连接
      if (!wsStore.isConnected) {
        await wsStore.connect()
      }

      // 发送 USLO00 登录消息
      // 完整参数列表:
      // 0. 用户名
      // 1. 密码
      // 2. 用户类型 ('5'=普通用户, '11'=游客)
      // 3. 网站版本
      // 4. 应用类型
      // 5. 设备ID
      // 6. 端口号
      // 7. 平台
      // 8. 备用IP
      const deviceId = getDeviceId()
      const platform = getPlatform()
      const config = window.APP_CONFIG || {}

      const { sendUSLO00 } = await import('@/services/websocket/senders')
      sendUSLO00(
        username, // 0. 用户名
        password, // 1. 密码
        userType, // 2. 用户类型
        config.websiteVersion || import.meta.env.VITE_WEBSITE_VERSION || 'v1.0.0', // 3. 网站版本
        config.appType || import.meta.env.VITE_APP_TYPE || 'H5', // 4. 应用类型
        deviceId, // 5. 设备ID
        '8015', // 6. 端口号
        platform, // 7. 平台
        '192.168.0.1' // 8. 备用IP
      )
    },

    /**
     * 游客试玩登录
     */
    async loginAsGuest() {
      // 游客登录: 用户名和密码为空,用户类型为 '11'
      await this.login('', '', '11')
    },

    /**
     * HTTP API 登录
     * 调用后端 HTTP 接口进行登录，后端会处理视讯系统的登录
     */
    async httpLogin(username: string, password: string): Promise<{ success: boolean; error?: string }> {
      const result = await authApi.login(username, password)

      if (result.success && result.data) {
        // 保存基本用户信息
        this.memberId = result.data.uid
        this.userName = username
        this.isLoggedIn = true
        this.userType = '5' // HTTP 登录为普通用户

        // 获取完整用户信息
        const userInfoResult = await authApi.getUserInfo()
        if (userInfoResult.success && userInfoResult.data) {
          this.userName = userInfoResult.data.nickname || username
        }

        // 持久化用户信息
        await this.persistUserInfo()

        return { success: true }
      }

      return { success: false, error: result.error }
    },

    /**
     * HTTP API 注册
     */
    async httpRegister(
      username: string,
      password: string,
      inviteCode?: string
    ): Promise<{ success: boolean; error?: string }> {
      const result = await authApi.register(username, password, inviteCode)

      if (result.success && result.data) {
        // 注册成功后自动登录
        this.memberId = result.data.uid
        this.userName = username
        this.isLoggedIn = true
        this.userType = '5'

        await this.persistUserInfo()

        return { success: true }
      }

      return { success: false, error: result.error }
    },

    /**
     * HTTP Google OAuth 登录
     * @param idToken Google GIS 返回的 id_token
     * @param inviteCode 邀请码（可选）
     */
    async httpGoogleLogin(idToken: string, inviteCode?: string): Promise<{ success: boolean; error?: string }> {
      const result = await authApi.loginWithGoogle(idToken, inviteCode)

      if (result.success && result.data) {
        this.memberId = result.data.uid
        this.userName = ''
        this.isLoggedIn = true
        this.userType = '5'

        // 获取完整用户信息
        const userInfoResult = await authApi.getUserInfo()
        if (userInfoResult.success && userInfoResult.data) {
          this.userName = userInfoResult.data.nickname || ''
        }

        await this.persistUserInfo()
        return { success: true }
      }

      return { success: false, error: result.error }
    },

    /**
     * HTTP 登出
     */
    httpLogout() {
      authApi.logout()
      // 同时清理本地状态
      this.logout()
    },

    /**
     * 从 HTTP API 恢复登录状态
     * 页面刷新时调用，检查 token 并获取用户信息
     */
    async restoreHttpLogin(): Promise<boolean> {
      // 检查本地是否有有效的 token
      if (authApi.isLoggedIn()) {
        // 获取用户信息来验证 token
        const result = await authApi.getUserInfo()
        if (result.success && result.data) {
          this.memberId = result.data.uid || ''
          this.userName = result.data.nickname || ''
          this.isLoggedIn = true
          this.userType = '5'
          await this.persistUserInfo()
          return true
        } else {
          // token 无效，清除
          authApi.logout()
        }
      }
      return false
    },

    /**
     * 从 USLO06 数据更新会员余额信息
     *
     * @param balanceAfterBetting 下注后的余额 (字符串格式)
     */
    updateBalanceFromUSLO06(balanceAfterBetting: string) {
      // 将字符串转换为数字
      const newBalance = Number(balanceAfterBetting) || 0

      if (this.balance !== newBalance) {
        this.balance = newBalance
        this.persistUserInfo()
      }
    },

    /**
     * 获取视频流地址
     *
     * @param gameId 游戏桌ID (对应 channelList 中的 tableNo)
     * @param videoType 视频类型 (默认 'beadChannel' 近景)
     * @param videoCenterId 视频中心ID (可选,默认使用最小的)
     * @returns 视频流地址,如果不可用则返回空字符串
     */
    getVideoUrl(gameId: string, videoType: keyof ChannelInfo = 'beadChannel', videoCenterId?: string): string {
      // 1. 查找服务器
      let server: VideoServer | undefined

      try {
        if (videoCenterId) {
          // 根据指定的 videoCenterId 查找
          server = this.serverList.find(s => s.videoCenterId === videoCenterId)
        } else if (this.serverList.length > 0) {
          // 选择 videoCenterId 最小的服务器
          server = this.serverList.reduce((prev, curr) => {
            return Number(curr.videoCenterId) < Number(prev.videoCenterId) ? curr : prev
          })
        }
      } catch (error) {
        console.error('[User] 获取视频服务器失败:', error)
        return ''
      }

      if (!server) {
        return ''
      }

      // 2. 查找频道信息
      const channel = this.channelList.find(ch => ch.tableNo === gameId)

      if (!channel) {
        return ''
      }

      // 3. 检查频道是否可播放
      const isPlayable = (value: string) => value !== '-1' && value !== '1024'

      // 4. 根据视频类型获取频道IP和频道值
      let channelIp = ''
      let channelValue = ''

      switch (videoType) {
        case 'beadChannel': // 近景
          channelIp = channel.beadIp
          channelValue = channel.beadChannel
          break
        case 'channel_1_7': // 远景1
          channelIp = channel.ip_1_7
          channelValue = channel.channel_1_7
          break
        case 'channel_8_15': // 远景2
          channelIp = channel.ip_8_15
          channelValue = channel.channel_8_15
          break
        case 'hallChannel_1': // 大厅1
          channelIp = channel.hallIp_1
          channelValue = channel.hallChannel_1
          break
        case 'hallChannel_2': // 大厅2
          channelIp = channel.hallIp_2
          channelValue = channel.hallChannel_2
          break
        default:
          return ''
      }

      // 5. 如果频道可播放,返回视频流地址
      if (isPlayable(channelValue)) {
        const url = `ws://${server.serverIp}:${server.serverPort}/${channelIp}.flv`
        return url
      }

      return ''
    },

    /**
     * 获取可播放的视频频道列表
     *
     * @param gameId 游戏桌ID
     * @returns 可播放的视频类型数组
     */
    getPlayableChannels(gameId: string): Array<keyof ChannelInfo> {
      const channel = this.channelList.find(ch => ch.tableNo === gameId)

      if (!channel) {
        return []
      }

      const isPlayable = (value: string) => value !== '-1' && value !== '1024'

      const channels: Array<keyof ChannelInfo> = []

      if (isPlayable(channel.beadChannel)) channels.push('beadChannel')
      if (isPlayable(channel.channel_1_7)) channels.push('channel_1_7')
      if (isPlayable(channel.channel_8_15)) channels.push('channel_8_15')
      if (isPlayable(channel.hallChannel_1)) channels.push('hallChannel_1')
      if (isPlayable(channel.hallChannel_2)) channels.push('hallChannel_2')

      return channels
    },
  },
})
